/**
 * Supabase 数据库客户端封装
 * Database Module - db.js
 * 提供所有数据表的 CRUD 操作
 */

import { createClient } from '@supabase/supabase-js'

// --- Supabase 客户端实例 ---
let supabase = null
let isDbAvailable = false

/**
 * 初始化 Supabase 客户端
 * 如果环境变量未配置，会优雅降级
 */
export function initDb() {
  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

  if (!url || !anonKey || url === 'https://xxx.supabase.co') {
    console.warn('[DB] Supabase 环境变量未配置，数据库功能将不可用')
    isDbAvailable = false
    return false
  }

  try {
    supabase = createClient(url, anonKey)
    isDbAvailable = true
    console.log('[DB] Supabase 客户端初始化成功')
    return true
  } catch (err) {
    console.error('[DB] Supabase 客户端初始化失败:', err)
    isDbAvailable = false
    return false
  }
}

/**
 * 获取 Supabase 客户端实例
 */
export function getDb() {
  return supabase
}

/**
 * 检查数据库是否可用
 */
export function isAvailable() {
  return isDbAvailable
}

/**
 * 显示数据库未连接提示
 */
export function showDbWarning(container) {
  if (isDbAvailable) return false

  const warning = document.createElement('div')
  warning.className = 'db-warning'
  warning.innerHTML = `
    <span class="warning-icon">⚠️</span>
    <span>数据库未连接。请在 .env 文件中配置 VITE_SUPABASE_URL 和 VITE_SUPABASE_ANON_KEY，数据将保存在本地。</span>
  `

  if (container) {
    container.prepend(warning)
  }
  return true
}

// ===================================
// 灵感速记 - inspiration 表
// ===================================
export const inspiration = {
  async create(data) {
    if (!isDbAvailable) return { data: { ...data, id: Date.now() }, error: null }
    return await supabase.from('inspiration').insert([data]).select()
  },

  async list(options = {}) {
    if (!isDbAvailable) return { data: [], error: null }
    const { limit = 50, offset = 0 } = options
    return await supabase
      .from('inspiration')
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)
  },

  async update(id, data) {
    if (!isDbAvailable) return { data, error: null }
    return await supabase.from('inspiration').update(data).eq('id', id).select()
  },

  async delete(id) {
    if (!isDbAvailable) return { error: null }
    return await supabase.from('inspiration').delete().eq('id', id)
  },
}

// ===================================
// 每日复盘 - daily_review 表
// ===================================
export const dailyReview = {
  async create(data) {
    if (!isDbAvailable) return { data: { ...data, id: Date.now() }, error: null }
    return await supabase.from('daily_review').insert([data]).select()
  },

  async getToday() {
    if (!isDbAvailable) return { data: null, error: null }
    const today = new Date().toISOString().split('T')[0]
    return await supabase
      .from('daily_review')
      .select('*')
      .eq('date', today)
      .single()
  },

  async list(options = {}) {
    if (!isDbAvailable) return { data: [], error: null }
    const { limit = 30, offset = 0 } = options
    return await supabase
      .from('daily_review')
      .select('*')
      .order('date', { ascending: false })
      .range(offset, offset + limit - 1)
  },

  async update(id, data) {
    if (!isDbAvailable) return { data, error: null }
    return await supabase.from('daily_review').update(data).eq('id', id).select()
  },

  async delete(id) {
    if (!isDbAvailable) return { error: null }
    return await supabase.from('daily_review').delete().eq('id', id)
  },
}

// ===================================
// 收支记录 - transaction 表
// ===================================
export const transaction = {
  async create(data) {
    if (!isDbAvailable) return { data: { ...data, id: Date.now() }, error: null }
    return await supabase.from('transaction').insert([data]).select()
  },

  async list(options = {}) {
    if (!isDbAvailable) return { data: [], error: null }
    const { limit = 100, offset = 0, startDate, endDate } = options
    let query = supabase
      .from('transaction')
      .select('*')
      .order('date', { ascending: false })
      .range(offset, offset + limit - 1)

    if (startDate) query = query.gte('date', startDate)
    if (endDate) query = query.lte('date', endDate)

    return await query
  },

  async getMonthlySummary(year, month) {
    if (!isDbAvailable) return { data: { income: 0, expense: 0 }, error: null }
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`
    const endDate = `${year}-${String(month).padStart(2, '0')}-31`
    return await supabase.rpc('get_monthly_summary', {
      start_date: startDate,
      end_date: endDate,
    })
  },

  async update(id, data) {
    if (!isDbAvailable) return { data, error: null }
    return await supabase.from('transaction').update(data).eq('id', id).select()
  },

  async delete(id) {
    if (!isDbAvailable) return { error: null }
    return await supabase.from('transaction').delete().eq('id', id)
  },
}

// ===================================
// 新闻热点 - news 表
// ===================================
export const news = {
  async create(data) {
    if (!isDbAvailable) return { data: { ...data, id: Date.now() }, error: null }
    return await supabase.from('news').insert([data]).select()
  },

  async list(options = {}) {
    if (!isDbAvailable) return { data: [], error: null }
    const { limit = 50, offset = 0, category } = options
    let query = supabase
      .from('news')
      .select('*')
      .order('published_at', { ascending: false })
      .range(offset, offset + limit - 1)

    if (category && category !== 'all') {
      query = query.eq('category', category)
    }

    return await query
  },

  async update(id, data) {
    if (!isDbAvailable) return { data, error: null }
    return await supabase.from('news').update(data).eq('id', id).select()
  },

  async delete(id) {
    if (!isDbAvailable) return { error: null }
    return await supabase.from('news').delete().eq('id', id)
  },
}

// ===================================
// 每日文摘 - quote 表
// ===================================
export const quote = {
  async create(data) {
    if (!isDbAvailable) return { data: { ...data, id: Date.now() }, error: null }
    return await supabase.from('daily_quotes').insert([data]).select()
  },

  async getToday() {
    if (!isDbAvailable) return { data: null, error: null }
    const today = new Date().toISOString().split('T')[0]
    return await supabase
      .from('daily_quotes')
      .select('*')
      .eq('published_date', today)
      .single()
  },

  async list(options = {}) {
    if (!isDbAvailable) return { data: [], error: null }
    const { limit = 50, offset = 0, category, isFavorited } = options
    let query = supabase
      .from('daily_quotes')
      .select('*')
      .order('published_date', { ascending: false })
      .range(offset, offset + limit - 1)

    if (category && category !== 'all') query = query.eq('category', category)
    if (isFavorited) query = query.eq('is_favorited', true)

    return await query
  },

  async update(id, data) {
    if (!isDbAvailable) return { data, error: null }
    return await supabase.from('daily_quotes').update(data).eq('id', id).select()
  },

  async delete(id) {
    if (!isDbAvailable) return { error: null }
    return await supabase.from('daily_quotes').delete().eq('id', id)
  },
}

// ===================================
// 课程 - course 表
// ===================================
export const course = {
  async create(data) {
    if (!isDbAvailable) return { data: { ...data, id: Date.now() }, error: null }
    return await supabase.from('course').insert([data]).select()
  },

  async list(options = {}) {
    if (!isDbAvailable) return { data: [], error: null }
    const { limit = 50, offset = 0, status } = options
    let query = supabase
      .from('course')
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)

    if (status) query = query.eq('status', status)

    return await query
  },

  async update(id, data) {
    if (!isDbAvailable) return { data, error: null }
    return await supabase.from('course').update(data).eq('id', id).select()
  },

  async delete(id) {
    if (!isDbAvailable) return { error: null }
    return await supabase.from('course').delete().eq('id', id)
  },
}

// ===================================
// 课程集 - course_episode 表
// ===================================
export const courseEpisode = {
  async create(data) {
    if (!isDbAvailable) return { data: { ...data, id: Date.now() }, error: null }
    return await supabase.from('course_episode').insert([data]).select()
  },

  async list(courseId) {
    if (!isDbAvailable) return { data: [], error: null }
    return await supabase
      .from('course_episode')
      .select('*')
      .eq('course_id', courseId)
      .order('episode_index', { ascending: true })
  },

  async update(id, data) {
    if (!isDbAvailable) return { data, error: null }
    return await supabase.from('course_episode').update(data).eq('id', id).select()
  },

  async delete(id) {
    if (!isDbAvailable) return { error: null }
    return await supabase.from('course_episode').delete().eq('id', id)
  },
}
